<script setup lang="ts">
import { onMounted, ref, computed } from 'vue'
import { generateDisplacementMap, generateSpecularHighlight, type SurfaceProfile } from '@/lib/liquid-glass-displacement'

const displacementMapUrl = ref('')
const specularHighlightUrl = ref('')
const maxDisplacement = ref(0)

// Different profiles for different UI elements
const profiles = ref<Record<string, { profile: SurfaceProfile; scale: number }>>({
  input: { profile: 'squircle', scale: 1.0 },
  dropdown: { profile: 'convex', scale: 0.8 },
  button: { profile: 'lip', scale: 0.6 },
  card: { profile: 'squircle', scale: 1.2 }
})

const isChrome = computed(() => {
  return /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor)
})

onMounted(() => {
  if (!isChrome.value) {
    // Don't generate maps for non-Chrome browsers
    return
  }

  // Generate displacement map with squircle profile (Apple's preference)
  const { dataUrl, maxDisplacement: max } = generateDisplacementMap({
    profile: 'squircle',
    refractiveIndex: 1.5,
    size: 300,
    samples: 127
  })

  displacementMapUrl.value = dataUrl
  maxDisplacement.value = max

  // Generate specular highlight with higher intensity for visible rim lighting
  specularHighlightUrl.value = generateSpecularHighlight(300, 0.9)
})
</script>

<template>
  <!-- SVG Filters for Liquid Glass Effect -->
  <!-- Only rendered in Chrome browsers that support backdrop-filter with SVG -->
  <svg
    v-if="isChrome && displacementMapUrl"
    width="0"
    height="0"
    style="position: absolute; pointer-events: none;"
    aria-hidden="true"
  >
    <defs>
      <!-- Main Liquid Glass Filter with Displacement -->
      <filter
        id="liquid-glass-displacement"
        x="-50%"
        y="-50%"
        width="200%"
        height="200%"
        color-interpolation-filters="sRGB"
      >
        <!-- Load displacement map -->
        <feImage
          :href="displacementMapUrl"
          result="displacement_map"
          preserveAspectRatio="none"
        />

        <!-- Apply displacement to create refraction effect -->
        <feDisplacementMap
          in="SourceGraphic"
          in2="displacement_map"
          :scale="maxDisplacement"
          xChannelSelector="R"
          yChannelSelector="G"
          result="displaced"
        />

        <!-- Add subtle blur for depth -->
        <feGaussianBlur
          in="displaced"
          stdDeviation="1"
          result="blurred"
        />

        <!-- Load specular highlight -->
        <feImage
          :href="specularHighlightUrl"
          result="specular"
          preserveAspectRatio="none"
        />

        <!-- Blend specular with displaced -->
        <feBlend
          in="blurred"
          in2="specular"
          mode="screen"
          result="final"
        />

        <feMerge>
          <feMergeNode in="final" />
        </feMerge>
      </filter>

      <!-- Lighter version for inputs -->
      <filter
        id="liquid-glass-input"
        x="-50%"
        y="-50%"
        width="200%"
        height="200%"
        color-interpolation-filters="sRGB"
      >
        <feImage
          :href="displacementMapUrl"
          result="displacement_map"
          preserveAspectRatio="none"
        />

        <feDisplacementMap
          in="SourceGraphic"
          in2="displacement_map"
          :scale="maxDisplacement * 0.6"
          xChannelSelector="R"
          yChannelSelector="G"
          result="displaced"
        />

        <feGaussianBlur
          in="displaced"
          stdDeviation="0.8"
          result="blurred"
        />

        <!-- Add specular highlight for rim lighting -->
        <feImage
          :href="specularHighlightUrl"
          result="specular"
          preserveAspectRatio="none"
        />

        <feBlend
          in="blurred"
          in2="specular"
          mode="screen"
          result="final"
        />
      </filter>

      <!-- Stronger version for dropdowns -->
      <filter
        id="liquid-glass-dropdown-advanced"
        x="-50%"
        y="-50%"
        width="200%"
        height="200%"
        color-interpolation-filters="sRGB"
      >
        <feImage
          :href="displacementMapUrl"
          result="displacement_map"
          preserveAspectRatio="none"
        />

        <feDisplacementMap
          in="SourceGraphic"
          in2="displacement_map"
          :scale="maxDisplacement * 1.2"
          xChannelSelector="R"
          yChannelSelector="G"
          result="displaced"
        />

        <feGaussianBlur
          in="displaced"
          stdDeviation="1.5"
          result="blurred"
        />

        <feImage
          :href="specularHighlightUrl"
          result="specular"
          preserveAspectRatio="none"
        />

        <feBlend
          in="blurred"
          in2="specular"
          mode="screen"
          result="final"
        />
      </filter>

      <!-- Button version with lip profile effect and saturation -->
      <filter
        id="liquid-glass-button"
        x="-50%"
        y="-50%"
        width="200%"
        height="200%"
        color-interpolation-filters="sRGB"
      >
        <feImage
          :href="displacementMapUrl"
          result="displacement_map"
          preserveAspectRatio="none"
        />

        <feDisplacementMap
          in="SourceGraphic"
          in2="displacement_map"
          :scale="maxDisplacement * 0.5"
          xChannelSelector="R"
          yChannelSelector="G"
          result="displaced"
        />

        <feGaussianBlur
          in="displaced"
          stdDeviation="0"
          result="blurred"
        />

        <!-- Add color saturation like in the article -->
        <feColorMatrix
          in="blurred"
          type="saturate"
          values="9"
          result="displaced_saturated"
        />

        <!-- Load specular highlight -->
        <feImage
          :href="specularHighlightUrl"
          result="specular_layer"
          preserveAspectRatio="none"
        />

        <!-- Composite specular with displaced -->
        <feComposite
          in="displaced_saturated"
          in2="specular_layer"
          operator="in"
          result="specular_saturated"
        />

        <!-- Fade specular layer -->
        <feComponentTransfer in="specular_layer" result="specular_faded">
          <feFuncA type="linear" slope="0.5" />
        </feComponentTransfer>

        <!-- Blend layers together -->
        <feBlend
          in="specular_saturated"
          in2="blurred"
          mode="normal"
          result="withSaturation"
        />

        <feBlend
          in="specular_faded"
          in2="withSaturation"
          mode="normal"
        />
      </filter>
    </defs>
  </svg>
</template>

<style scoped>
/* Ensure SVG doesn't interfere with layout */
svg {
  position: absolute;
  width: 0;
  height: 0;
  overflow: hidden;
}
</style>
